import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { getPosLocationCurrency } from "../common/helpers/function";
import DashboardChartCardKebabMenu from "./DashboardChartCardKebabMenu";
import { DashboardChartTableSortHeader } from "./DashboardChartTableSortHeader";
import { mergeCustomOrderModel } from "./dashboardChartsModel";
import { buildTimestampedPngFilename, downloadElementAsPng } from "./downloadCapturePng";
import "./dashboardChartJsSetup";

const COLOR_AMOUNT = "#5C74C7";
const COLOR_QTY = "#5FBEAA";
const COLOR_CUSTOMER = "#4E8EF0";

function formatAmountCell(n) {
  const v = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(v)) return "";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Math.round(v));
}

function formatSaleTooltipThou(thousands) {
  const v = typeof thousands === "number" ? thousands : Number(thousands);
  if (!Number.isFinite(v)) return "0K";
  const rounded = Math.abs(v - Math.round(v)) < 1e-6 ? Math.round(v) : Math.round(v * 10) / 10;
  return `${rounded}K`;
}

/** Integer max + step for qty/customer (right) axis — avoids fractional ticks. */
function countAxisIntegerMaxAndStep(maxVal, tickTarget = 6) {
  const m = Math.max(0, Number(maxVal) || 0);
  const padded = Math.max(1, Math.ceil(m * 1.25));
  const naive = Math.ceil(padded / Math.max(2, tickTarget - 1));
  let step = Math.max(1, naive);
  const nice = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 2500, 5000, 10000];
  const hit = nice.find((n) => n >= naive);
  if (hit != null) step = hit;
  const axisMax = Math.max(padded, Math.ceil(padded / step) * step);
  return { max: axisMax, stepSize: step };
}

/**
 * @param {{ model: import("./dashboardChartsModel.js").CustomOrderChartModel; cardTitle: string }} props
 */
function OrderMetricsChartBody({ model, cardTitle }) {
  const { t } = useTranslation();
  const currencyCode = getPosLocationCurrency();

  const { chartData, options } = useMemo(() => {
    const labels = model.categories ?? [];
    const amount = model.amount ?? [];
    const qty = model.qty ?? [];
    const customer = model.customer ?? [];
    const n = labels.length;
    const amountK = Array.from({ length: n }, (_, i) => {
      const th = Math.round((Number(amount[i]) || 0) / 1000);
      return Number.isFinite(th) ? th : 0;
    });
    const qtyN = Array.from({ length: n }, (_, i) => Math.round(Number(qty[i]) || 0));
    const custN = Array.from({ length: n }, (_, i) => Math.round(Number(customer[i]) || 0));

    const peakAmountK = Math.max(120, ...amountK, 0);
    const yMax = Math.ceil(peakAmountK / 20) * 20;

    const maxCount = Math.max(0, ...qtyN, ...custN, 0);
    const { max: yRightMax, stepSize: yRightStep } = countAxisIntegerMaxAndStep(maxCount);

    const dsAmount = t("Amount", { defaultValue: "Amount" });
    const dsQty = t("Qty", { defaultValue: "Qty" });
    const dsCustomer = t("Customer", { defaultValue: "Customer" });
    const rightAxisTitle = t("dashboardQtyCustomerAxis", { defaultValue: "Qty / Customer" });

    const data = {
      labels,
      datasets: [
        {
          label: dsAmount,
          data: amountK,
          backgroundColor: COLOR_AMOUNT,
          borderRadius: 0,
          borderSkipped: false,
          maxBarThickness: 36,
          yAxisID: "y",
          order: 1,
        },
        {
          label: dsQty,
          data: qtyN,
          backgroundColor: COLOR_QTY,
          borderRadius: 0,
          borderSkipped: false,
          maxBarThickness: 36,
          yAxisID: "y1",
          order: 2,
        },
        {
          label: dsCustomer,
          data: custN,
          backgroundColor: COLOR_CUSTOMER,
          borderRadius: 0,
          borderSkipped: false,
          maxBarThickness: 36,
          yAxisID: "y1",
          order: 3,
        },
      ],
    };

    const opts = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      layout: {
        padding: {
          top: 4,
          right: 8,
          left: 2,
          bottom: 36,
        },
      },
      datasets: {
        bar: {
          categoryPercentage: 0.72,
          barPercentage: 0.85,
        },
      },
      plugins: {
        legend: {
          position: "bottom",
          align: "center",
          labels: {
            boxWidth: 10,
            boxHeight: 10,
            usePointStyle: true,
            padding: 16,
            color: "#000000",
            font: {
              family: "'Segoe UI', Roboto, sans-serif",
              size: 12,
              weight: "400",
            },
          },
        },
        tooltip: {
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          titleColor: "#2A2A2A",
          bodyColor: "#2A2A2A",
          borderColor: "#D9D9D9",
          borderWidth: 1,
          padding: 12,
          cornerRadius: 8,
          displayColors: true,
          titleFont: { family: "'Segoe UI', Roboto, sans-serif", size: 12, weight: "600" },
          bodyFont: { family: "'Segoe UI', Roboto, sans-serif", size: 12, weight: "400" },
          callbacks: {
            title: (items) => (items[0] ? String(items[0].label) : ""),
            label: (ctx) => {
              const y = ctx.parsed?.y ?? 0;
              if (ctx.datasetIndex === 0) {
                return ` ${dsAmount}: ${formatSaleTooltipThou(y)}`;
              }
              if (ctx.datasetIndex === 1) {
                return ` ${dsQty}: ${y}`;
              }
              return ` ${dsCustomer}: ${y}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            color: "#757575",
            font: { family: "Helvetica, 'Segoe UI', Roboto, sans-serif", size: 11 },
            maxRotation: n > 7 ? 35 : 0,
            minRotation: 0,
          },
          border: { display: false },
        },
        y: {
          type: "linear",
          position: "left",
          min: 0,
          max: yMax,
          title: {
            display: true,
            text: t("dashboardCurrencyThousandsAxis", {
              currency: currencyCode,
              defaultValue: "{{currency}} (thousands)",
            }),
            color: "#373D3F",
            font: { family: "Helvetica, 'Segoe UI', Roboto, sans-serif", size: 11, weight: "700" },
          },
          ticks: {
            stepSize: 20,
            color: "#373D3F",
            font: { family: "Helvetica, 'Segoe UI', Roboto, sans-serif", size: 11 },
          },
          grid: {
            color: "#E0E0E0",
            drawTicks: false,
          },
          border: { display: false },
        },
        y1: {
          type: "linear",
          position: "right",
          min: 0,
          max: yRightMax,
          title: {
            display: true,
            text: rightAxisTitle,
            color: "#373D3F",
            font: { family: "Helvetica, 'Segoe UI', Roboto, sans-serif", size: 11, weight: "700" },
          },
          ticks: {
            stepSize: yRightStep,
            color: "#373D3F",
            font: { family: "Helvetica, 'Segoe UI', Roboto, sans-serif", size: 11 },
            callback: (v) => {
              const num = typeof v === "number" ? v : Number(v);
              if (!Number.isFinite(num)) return "";
              return String(Math.round(num));
            },
          },
          grid: {
            drawOnChartArea: false,
            drawTicks: false,
          },
          border: { display: false },
        },
      },
    };

    return { chartData: data, options: opts };
  }, [model, t, currencyCode]);

  return (
    <div className="pos-custom-order-chart" aria-label={cardTitle}>
      <div className="pos-custom-order-chart__plot">
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
}

/**
 * @param {{ model: import("./dashboardChartsModel.js").CustomOrderChartModel; cardTitle: string; rowIdPrefix: string }} props
 */
function OrderMetricsTableBody({ model, cardTitle, rowIdPrefix }) {
  const { t } = useTranslation();
  const [sort, setSort] = useState(() => ({
    key: /** @type {"category"|"qty"|"customer"|"amount"} */ ("amount"),
    dir: /** @type {"asc"|"desc"} */ ("desc"),
  }));

  useEffect(() => {
    setSort({ key: "amount", dir: "desc" });
  }, [model]);

  const toggleSort = useCallback((key) => {
    setSort((s) => (s.key !== key ? { key, dir: "asc" } : { key, dir: s.dir === "asc" ? "desc" : "asc" }));
  }, []);

  const baseRows = useMemo(() => {
    const cat = model?.categories ?? [];
    const amt = model?.amount ?? [];
    const q = model?.qty ?? [];
    const c = model?.customer ?? [];
    const n = cat.length;
    const rows = [];
    for (let i = 0; i < n; i += 1) {
      rows.push({
        key: `${rowIdPrefix}-row-${i}-${cat[i]}`,
        category: cat[i] ?? "",
        amount: Number(amt[i]) || 0,
        qty: Math.round(Number(q[i]) || 0),
        customer: Math.round(Number(c[i]) || 0),
      });
    }
    return rows;
  }, [model, rowIdPrefix]);

  const rows = useMemo(() => {
    const copy = baseRows.map((r) => ({ ...r }));
    const mul = sort.dir === "asc" ? 1 : -1;
    copy.sort((a, b) => {
      let cmp = 0;
      switch (sort.key) {
        case "category":
          cmp = String(a.category).localeCompare(String(b.category), undefined, { sensitivity: "base" });
          break;
        case "qty":
          cmp = a.qty - b.qty;
          break;
        case "customer":
          cmp = a.customer - b.customer;
          break;
        case "amount":
          cmp = a.amount - b.amount;
          break;
        default:
          cmp = 0;
      }
      return cmp * mul;
    });
    return copy;
  }, [baseRows, sort]);

  const totals = useMemo(() => {
    let amount = 0;
    let qty = 0;
    let customer = 0;
    for (const r of baseRows) {
      amount += r.amount;
      qty += r.qty;
      customer += r.customer;
    }
    return { amount, qty, customer };
  }, [baseRows]);

  const thBase = "pos-custom-order-table__th";
  const thSorted = "pos-custom-order-table__th--sorted";

  return (
    <div className="pos-custom-order-table" role="table" aria-label={cardTitle}>
      <div className="pos-custom-order-table__head" role="rowgroup">
        <div className="pos-custom-order-table__tr pos-custom-order-table__tr--head" role="row">
          <DashboardChartTableSortHeader
            baseClassName={thBase}
            sortedClassName={thSorted}
            className="pos-custom-order-table__th--category"
            ariaSortLabel={t("Sort by category", { defaultValue: "Sort by category" })}
            sorted={sort.key === "category"}
            direction={sort.dir}
            onSort={() => toggleSort("category")}
          >
            {t("Category", { defaultValue: "Category" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            baseClassName={thBase}
            sortedClassName={thSorted}
            className="pos-custom-order-table__th--num"
            ariaSortLabel={t("Sort by quantity", { defaultValue: "Sort by quantity" })}
            sorted={sort.key === "qty"}
            direction={sort.dir}
            onSort={() => toggleSort("qty")}
          >
            {t("Qty", { defaultValue: "Qty" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            baseClassName={thBase}
            sortedClassName={thSorted}
            className="pos-custom-order-table__th--num"
            ariaSortLabel={t("Sort by customer", { defaultValue: "Sort by customer" })}
            sorted={sort.key === "customer"}
            direction={sort.dir}
            onSort={() => toggleSort("customer")}
          >
            {t("Customer", { defaultValue: "Customer" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            baseClassName={thBase}
            sortedClassName={thSorted}
            className="pos-custom-order-table__th--num pos-custom-order-table__th--last"
            ariaSortLabel={t("Sort by amount", { defaultValue: "Sort by amount" })}
            sorted={sort.key === "amount"}
            direction={sort.dir}
            onSort={() => toggleSort("amount")}
          >
            {t("Amount", { defaultValue: "Amount" })}
          </DashboardChartTableSortHeader>
        </div>
      </div>
      <div className="pos-custom-order-table__scroll" role="rowgroup">
        {rows.map((row) => (
          <div key={row.key} className="pos-custom-order-table__tr" role="row">
            <div className="pos-custom-order-table__td pos-custom-order-table__td--category" role="cell">
              {row.category}
            </div>
            <div className="pos-custom-order-table__td pos-custom-order-table__td--num" role="cell">
              {row.qty}
            </div>
            <div className="pos-custom-order-table__td pos-custom-order-table__td--num" role="cell">
              {row.customer}
            </div>
            <div className="pos-custom-order-table__td pos-custom-order-table__td--num" role="cell">
              {formatAmountCell(row.amount)}
            </div>
          </div>
        ))}
        <div className="pos-custom-order-table__tr pos-custom-order-table__tr--total" role="row">
          <div className="pos-custom-order-table__td pos-custom-order-table__td--category" role="cell">
            {t("Total", { defaultValue: "Total" })}
          </div>
          <div className="pos-custom-order-table__td pos-custom-order-table__td--num" role="cell">
            {totals.qty}
          </div>
          <div className="pos-custom-order-table__td pos-custom-order-table__td--num" role="cell">
            {totals.customer}
          </div>
          <div className="pos-custom-order-table__td pos-custom-order-table__td--num" role="cell">
            {formatAmountCell(totals.amount)}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Shared grouped bar + table (Custom Order / Repair Order — same layout and SCSS).
 *
 * @param {object} props
 * @param {import("./dashboardChartsModel.js").CustomOrderChartModel | undefined} [props.model]
 * @param {() => void} [props.onMenuRemove]
 * @param {(patch: import("./dashboardChartsModel.js").CustomOrderChartModel | null | undefined) => import("./dashboardChartsModel.js").CustomOrderChartModel} props.mergeModel
 * @param {string} props.titleKey i18n key for card title
 * @param {string} props.defaultTitle
 * @param {string} props.downloadPrefix PNG filename prefix
 * @param {string} props.rowIdPrefix prefix for stable row keys (`co` | `ro`)
 */
export function OrderMetricsChartCard({ model, onMenuRemove, mergeModel, titleKey, defaultTitle, downloadPrefix, rowIdPrefix }) {
  const { t } = useTranslation();
  const captureRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const [viewMode, setViewMode] = useState(/** @type {"chart"|"table"} */ ("chart"));
  const merged = useMemo(() => mergeModel(model && typeof model === "object" ? model : null), [model, mergeModel]);
  const cardTitle = t(titleKey, { defaultValue: defaultTitle });

  const handleDownloadPng = useCallback(async () => {
    await downloadElementAsPng(captureRef.current, buildTimestampedPngFilename(downloadPrefix));
  }, [downloadPrefix]);

  return (
    <div
      className={`pos-dashboard-chart-card pos-dashboard-chart-card--custom-order${
        viewMode === "table" ? " pos-dashboard-chart-card--custom-order-table" : ""
      }`}
    >
      <header className="pos-dashboard-chart-card__header">
        <h2 className="pos-dashboard-chart-card__title">{cardTitle}</h2>
        <div className="pos-dashboard-chart-card__header-actions">
          <DashboardChartCardKebabMenu
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            onDownload={handleDownloadPng}
            onRemove={onMenuRemove}
            showViewToggle
          />
        </div>
      </header>
      <div
        className={`pos-dashboard-chart-card__body pos-dashboard-chart-card__body--custom-order${
          viewMode === "table" ? " pos-dashboard-chart-card__body--custom-order-table" : ""
        }`}
      >
        <div ref={captureRef} className="pos-dashboard-chart-card__capture pos-dashboard-chart-card__capture--custom-order">
          {viewMode === "chart" ? <OrderMetricsChartBody model={merged} cardTitle={cardTitle} /> : null}
          {viewMode === "table" ? (
            <OrderMetricsTableBody model={merged} cardTitle={cardTitle} rowIdPrefix={rowIdPrefix} />
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * Custom Order — grouped bar (Amount / Qty / Customer) + sortable table + kebab Chart / Table / Download / Remove.
 *
 * @param {object} props
 * @param {import("./dashboardChartsModel.js").CustomOrderChartModel | undefined} [props.model]
 * @param {() => void} [props.onMenuRemove]
 */
function CustomOrderChartCard({ model, onMenuRemove }) {
  return (
    <OrderMetricsChartCard
      model={model}
      onMenuRemove={onMenuRemove}
      mergeModel={mergeCustomOrderModel}
      titleKey="graphCustomOrder"
      defaultTitle="Custom Order"
      downloadPrefix="custom-order"
      rowIdPrefix="co"
    />
  );
}

export default CustomOrderChartCard;
